import React, { useState } from "react";

interface SelectBoxProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  invalid?: boolean;
  dropdownDirection?: "down" | "up";
}

export default function SelectBox({
  label,
  value,
  onChange,
  options,
  placeholder = "선택",
  className = "",
  disabled = false,
  required = false,
  invalid = false,
  dropdownDirection = "down",
}: SelectBoxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const hasLabel = label !== undefined;

  const handleOptionClick = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const selectedOption = options.find(option => option.value === value);

  return (
    <div className={`relative ${className}`}>
      {/* Label - 속성 존재 시에만 영역 생성 */}
      {hasLabel && (
        <div className="h-6 mb-1">
          <label className="block text-base font-normal text-[#414651]">
            {required && <span className="text-red-500 mr-1">*</span>}
            {label}
          </label>
        </div>
      )}

      {/* Select Box */}
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          aria-invalid={invalid || undefined} 
          className={`
            w-full bg-white border border-[#CCCCCC] rounded-md px-2 py-2 text-left
            focus:outline-none focus:ring-2 focus:ring-[#55b938] focus:border-[#55b938]
            ${disabled ? "bg-[#F6F6F6]! cursor-not-allowed" : "cursor-pointer hover:border-gray-400"}
            flex items-center justify-between
            ${invalid ? "border-[#FF3232]" : "border-[#CCCCCC]"}
          `}
        >
          <span className={selectedOption ? "text-[#333333]" : "text-[#717680]"}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>

          {/* Arrow Icon */}
          <img
            src="/icons/headerArrowBlack.svg"
            className="absolute right-2 top-1/2 -translate-y-1/2 h-[20px] w-[20px]"
          />
        </button>

        {/* Dropdown Options */}
        {isOpen && (
          <div
            className={`
              absolute z-60 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto
              ${dropdownDirection === "up" ? "bottom-full mb-1" : "top-full mt-1"}
            `}
          >
            {options.map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleOptionClick(option.value)}
                className={`
                  w-full px-3 py-2 text-left hover:bg-gray-100 focus:outline-none focus:bg-gray-100
                  ${value === option.value ? "bg-blue-50 text-blue-600" : "text-gray-900"}
                `}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Backdrop to close dropdown */}
      {isOpen && <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />}
    </div>
  );
}
